import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { FaArrowRight, FaCheck, FaComments, FaMobileAlt, FaHeadset, FaCreditCard, FaCode, FaUsers, FaQuoteRight } from 'react-icons/fa';
import { useParams } from "react-router-dom";
import bulksms from '../assets/bulksms.png'
import shortcode from '../assets/shortcode.png'
import ussd from '../assets/ussd.jpeg'
import callcenter from '../assets/callcenter.png'
import software from '../assets/software.jpeg'
import integration from '../assets/integration.jpeg'
import payment from '../assets/payment.jpeg'
import ivr from '../assets/ivr.png'
import LoadingSpinner from '../components/LoadingSpinner';
import { useApiData } from '../hooks/useApiData';
import { fetchServicePage, fetchImage } from '../repository/api';


const ServiceDetailPage = ({ match }) => {
  // In a real app: const serviceId = match.params.id;
  const { serviceId } = useParams();// Example service ID

  // Fetch service page data from API
  const { data: apiData, loading: apiLoading, error: apiError } = useApiData(fetchServicePage);

  const [isLoading, setIsLoading] = useState(true);
  const [service, setService] = useState(null);
  const [visibleElements, setVisibleElements] = useState({});
  const sectionRefs = useRef({});

  // Handle scroll-based animations
  useEffect(() => {
    console.log("Setting up Intersection Observer for sections");
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setVisibleElements(prev => ({
              ...prev,
              [entry.target.id]: true
            }));
          }
        });
      },
      { threshold: 0.1 }
    );

    Object.values(sectionRefs.current).forEach(ref => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  // Transform API data to match component structure
  const transformApiDataToService = (apiData) => {
    if (!apiData) return null;

    // Get a random service from the hardcoded data for related services
    const randomServiceIndex = Math.floor(Math.random() * servicesData.length);
    const randomService = servicesData[randomServiceIndex];

    return {
      id: serviceId || 'api-service',
      title: apiData.page_title || 'Our Service',
      shortDescription: apiData.page_paragraph || 'Comprehensive service solutions',
      description: apiData.page_paragraph || 'Our comprehensive service solution enables businesses to achieve their goals with high efficiency and reliability.',
      color: randomService.color || '#105090',
      iconComponent: randomService.iconComponent || 'FaComments',
      benefitsImage: randomService.benefitsImage || bulksms,
      features: (apiData.what_we_offer?.items || []).slice(0, 6).map((item, index) => ({
        title: item,
        description: `Powerful ${item.toLowerCase()} feature for your business needs.`,
        icon: randomService.features[index]?.icon || 'FaCheck'
      })),
      process: (apiData.how_it_works?.steps || []).map((step, index) => ({
        title: `Step ${index + 1}`,
        description: step
      })),
      benefits: (apiData.benefits?.items || []).map((benefit, index) => ({
        title: benefit,
        description: `Experience the benefits of ${benefit.toLowerCase()} with our service.`
      })),
      useCases: (apiData.use_cases?.items || []).map((useCase, index) => ({
        title: useCase,
        description: `Perfect for ${useCase.toLowerCase()} applications.`,
        icon: randomService.useCases[index]?.icon || 'FaCheck'
      })),
      testimonials: (apiData.testimonials || []).map((testimonial) => ({
        quote: testimonial.message,
        author: testimonial.name,
        position: testimonial.role,
        company: testimonial.company || 'Client Company',
        image: testimonial.photo ? fetchImage(testimonial.photo) : randomService.testimonials[0]?.image
      })),
      faqs: (apiData.faqs?.items || []).map((faq) => ({
        question: faq.question,
        answer: faq.answer
      }))
    };
  };

  // Set service data from API or fallback to hardcoded
  useEffect(() => {
    if (apiLoading) {
      setIsLoading(true);
      return;
    }

    // Try to use API data first
    let serviceData = null;
    if (apiData && !apiError) {
      serviceData = transformApiDataToService(apiData);
    }

    // Fallback to hardcoded data if API failed or no data
    if (!serviceData) {
      const foundService = servicesData.find(s => s.id === serviceId);
      serviceData = foundService || servicesData[0]; // Fallback to first service if not found
    }

    setService(serviceData);
    setIsLoading(false);
  }, [apiData, apiLoading, apiError, serviceId]);

  // Helper function to get icon component
  const getIconComponent = (iconName) => {
    const icons = {
      'FaComments': <FaComments />,
      'FaMobileAlt': <FaMobileAlt />,
      'FaHeadset': <FaHeadset />,
      'FaCreditCard': <FaCreditCard />,
      'FaCode': <FaCode />,
      'FaUsers': <FaUsers />
    };
    return icons[iconName] || <FaComments />;
  };

  if (isLoading) {
    return <LoadingSpinner/>;
  }

  if (!service) {
    return <ErrorMessage>Service not found</ErrorMessage>;
  }

  console.log("service", service);

  // Get related services (random selection, exclude current service)
  const getRandomRelatedServices = () => {
    const availableServices = servicesData.filter(s => s.id !== service.id);
    const shuffled = [...availableServices].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, 3);
  };
  
  const relatedServices = getRandomRelatedServices();

  return (
    <PageWrapper>
      {/* Hero Section */}
      <HeroSection
        bgColor={service.color}
        id="service-hero"
        ref={el => sectionRefs.current.hero = el}
        isVisible={true}
      >
        <Container>
          <HeroContent>
            <BreadcrumbNav>
              <BreadcrumbLink href="/">Home</BreadcrumbLink>
              <BreadcrumbSeparator>/</BreadcrumbSeparator>
              <BreadcrumbLink href="/services">Services</BreadcrumbLink>
              <BreadcrumbSeparator>/</BreadcrumbSeparator>
              <BreadcrumbCurrent>{service.title}</BreadcrumbCurrent>
            </BreadcrumbNav>

            <ServiceIcon>
              {getIconComponent(service.iconComponent)}
            </ServiceIcon>

            <HeroTitle>{service.title}</HeroTitle>

            <HeroDescription>{service.description}</HeroDescription>
          </HeroContent>
        </Container>
      </HeroSection>

      {/* Features Section */}
      <FeaturesSection
        id="service-features"
        ref={el => sectionRefs.current.features = el}
        isVisible={true}
      >
        <Container>
          <SectionHeader>
            <SectionSubtitle>KEY FEATURES</SectionSubtitle>
            <SectionTitle>What Our {service.title} Offers</SectionTitle>
            <SectionDescription>
              Discover the powerful features and benefits that make our {service.title.toLowerCase()} service the choice of leading businesses.
            </SectionDescription>
          </SectionHeader>

          <FeaturesGrid>
            {service.features.map((feature, index) => (
              <FeatureCard
                key={index}
                isVisible={true}
                delay={index * 0.1}
                color={service.color}
              >
                <FeatureIcon color={service.color}>
                  {getIconComponent(feature.icon)}
                </FeatureIcon>
                <FeatureTitle>{feature.title}</FeatureTitle>
                <FeatureDescription>{feature.description}</FeatureDescription>
              </FeatureCard>
            ))}
          </FeaturesGrid>
        </Container>
      </FeaturesSection>

      {/* How It Works Section */}
      <HowItWorksSection
        id="service-how-it-works"
        ref={el => sectionRefs.current.howItWorks = el}
        isVisible={true}

      >
        <Container>
          <SectionHeader>
            <SectionSubtitle>PROCESS</SectionSubtitle>
            <SectionTitle>How {service.title} Works</SectionTitle>
            <SectionDescription>
              A simple and straightforward process to get you started with our {service.title.toLowerCase()} service.
            </SectionDescription>
          </SectionHeader>

          <ProcessSteps>
            {service.process.map((step, index) => (
              <ProcessStep
                key={index}
                isVisible={true}
                delay={index * 0.2}
              >
                <StepNumber color={service.color}>{index + 1}</StepNumber>
                <StepContent>
                  <StepTitle>{step.title}</StepTitle>
                  <StepDescription>{step.description}</StepDescription>
                </StepContent>
              </ProcessStep>
            ))}
          </ProcessSteps>
        </Container>
      </HowItWorksSection>

      {/* Benefits Section */}
      <BenefitsSection
        id="service-benefits"
        ref={el => sectionRefs.current.benefits = el}
        isVisible={true}
      >
        <Container>
          <TwoColumnLayout>
            <BenefitsContent>
              <SectionSubtitle>WHY CHOOSE US</SectionSubtitle>
              <SectionTitle>Benefits of Our {service.title}</SectionTitle>
              <BenefitsList>
                {service.benefits.map((benefit, index) => (
                  <BenefitItem
                    key={index}
                    isVisible={true}
                    delay={index * 0.1}
                    color={service.color}
                  >
                    <BenefitCheck color={service.color}><FaCheck /></BenefitCheck>
                    <div>
                      <BenefitTitle>{benefit.title}</BenefitTitle>
                      <BenefitDescription>{benefit.description}</BenefitDescription>
                    </div>
                  </BenefitItem>
                ))}
              </BenefitsList>
            </BenefitsContent>

            <BenefitsImage>
              <img src={service.benefitsImage} alt={`${service.title} Benefits`} />
            </BenefitsImage>
          </TwoColumnLayout>
        </Container>
      </BenefitsSection>

      {/* Use Cases Section */}
      <UseCasesSection
        id="service-use-cases"
        ref={el => sectionRefs.current.useCases = el}
        isVisible={true}
      >
        <Container>
          <SectionHeader>
            <SectionSubtitle>APPLICATIONS</SectionSubtitle>
            <SectionTitle>Common Use Cases</SectionTitle>
            <SectionDescription>
              Discover how businesses are leveraging our {service.title.toLowerCase()} service to achieve their goals.
            </SectionDescription>
          </SectionHeader>

          <UseCasesGrid>
            {service.useCases.map((useCase, index) => (
              <UseCaseCard
                key={index}
                isVisible={true}
                delay={index * 0.1}
              >
                <UseCaseIcon color={service.color}>
                  {getIconComponent(useCase.icon)}
                </UseCaseIcon>
                <UseCaseTitle>{useCase.title}</UseCaseTitle>
                <UseCaseDescription>{useCase.description}</UseCaseDescription>
              </UseCaseCard>
            ))}
          </UseCasesGrid>
        </Container>
      </UseCasesSection>

      {/* Testimonials Section */}
      <TestimonialsSection
        id="service-testimonials"
        ref={el => sectionRefs.current.testimonials = el}
        isVisible={true}
        bgColor={service.color}
      >
        <Container>
          <SectionHeader light>
            <SectionSubtitle light>SUCCESS STORIES</SectionSubtitle>
            <SectionTitle light>What Our Clients Say</SectionTitle>
          </SectionHeader>

          <TestimonialsGrid>
            {service.testimonials.map((testimonial, index) => (
              <TestimonialCard
                key={index}
                isVisible={true}
                delay={index * 0.2}
              >
                <QuoteIcon><FaQuoteRight /></QuoteIcon>
                <TestimonialText>{testimonial.quote}</TestimonialText>
                <TestimonialAuthorSection>
                  <TestimonialAuthorImage src={testimonial.image} alt={testimonial.author} />
                  <TestimonialAuthorInfo>
                    <TestimonialAuthor>{testimonial.author}</TestimonialAuthor>
                    <TestimonialCompany>{testimonial.position}, {testimonial.company}</TestimonialCompany>
                  </TestimonialAuthorInfo>
                </TestimonialAuthorSection>
              </TestimonialCard>
            ))}
          </TestimonialsGrid>
        </Container>
      </TestimonialsSection>



      {/* FAQ Section */}
      <FAQSection
        id="service-faq"
        ref={el => sectionRefs.current.faq = el}
        isVisible={true}
      >
        <Container>
          <SectionHeader>
            <SectionSubtitle>QUESTIONS ANSWERED</SectionSubtitle>
            <SectionTitle>Frequently Asked Questions</SectionTitle>
          </SectionHeader>

          <FAQGrid>
            {service.faqs.map((faq, index) => (
              <FAQ
                key={index}
                isVisible={true}
                delay={index * 0.1}
              >
                <FAQQuestion>{faq.question}</FAQQuestion>
                <FAQAnswer>{faq.answer}</FAQAnswer>
              </FAQ>
            ))}
          </FAQGrid>

          <MoreQuestionsSection>
            <MoreQuestionsText>Have more questions about our {service.title}?</MoreQuestionsText>
            <MoreQuestionsButton href="/contact" color={service.color}>Contact Us</MoreQuestionsButton>
          </MoreQuestionsSection>
        </Container>
      </FAQSection>

      {/* Related Services */}
      <RelatedServicesSection
        id="service-related"
        ref={el => sectionRefs.current.related = el}
        isVisible={true}
      >
        <Container>
          <SectionHeader>
            <SectionSubtitle>EXPLORE MORE</SectionSubtitle>
            <SectionTitle>Related Services</SectionTitle>
            <SectionDescription>
              Discover other services that complement {service.title}
            </SectionDescription>
          </SectionHeader>

          <RelatedServicesGrid>
            {relatedServices.map((relatedService, index) => (
              <RelatedServiceCard
                key={relatedService.id}
                isVisible={true}
                delay={index * 0.1}
              >
                <RelatedServiceIcon color={relatedService.color}>
                  {getIconComponent(relatedService.iconComponent)}
                </RelatedServiceIcon>
                <RelatedServiceName>{relatedService.title}</RelatedServiceName>
                <RelatedServiceDescription>{relatedService.shortDescription}</RelatedServiceDescription>
                <RelatedServiceLink href={`/services/${relatedService.id}`} color={relatedService.color}>
                  Learn more <FaArrowRight />
                </RelatedServiceLink>
              </RelatedServiceCard>
            ))}
          </RelatedServicesGrid>
        </Container>
      </RelatedServicesSection>

      {/* CTA Section */}
      <CTASection
        id="service-cta"
        ref={el => sectionRefs.current.cta = el}
        isVisible={true}
        bgColor={service.color}
      >
        <Container>
          <CTAContent>
            <CTATitle>Ready to get started with {service.title}?</CTATitle>
            <CTADescription>
              Contact our team today to learn more about how our {service.title.toLowerCase()} service can help your business.
            </CTADescription>
            <CTAButton href="/contact">Get in Touch</CTAButton>
          </CTAContent>
        </Container>
      </CTASection>
    </PageWrapper>
  );
};

export default ServiceDetailPage;

// Detailed service data
const servicesData = [
  {
    id: 'bulksms',
    title: 'Bulk SMS',
    shortDescription: 'Reach customers instantly with high-deliverability SMS campaigns',
    description: 'Our comprehensive Bulk SMS solution enables businesses to send personalized messages to thousands of customers simultaneously, ensuring high deliverability and engagement rates.',
    color: '#105090',
    iconComponent: 'FaComments',
    benefitsImage: bulksms,
    features: [
      {
        title: 'Fast Message Delivery',
        description: 'Send thousands of messages in minutes with our high-throughput SMS gateway.',
        icon: 'FaRocket'
      },
      {
        title: 'Advanced Personalization',
        description: 'Create personalized messages using customer data fields and segmentation.',
        icon: 'FaUsers'
      },
      {
        title: 'Scheduled Campaigns',
        description: 'Plan and schedule your SMS campaigns in advance for optimal timing.',
        icon: 'FaClock'
      },
      {
        title: 'Delivery Reports',
        description: 'Access detailed delivery reports and analytics for campaign performance.',
        icon: 'FaChartBar'
      },
      {
        title: 'Two-way Messaging',
        description: 'Enable two-way communication with customers for improved engagement.',
        icon: 'FaComments'
      },
      {
        title: 'API Integration',
        description: 'Easily integrate with your existing systems using our robust API.',
        icon: 'FaCode'
      }
    ],
    process: [
      {
        title: 'Sign Up',
        description: 'Create an account and select a suitable plan for your business needs.'
      },
      {
        title: 'Upload Contacts',
        description: 'Import your contact list or create segments based on customer attributes.'
      },
      {
        title: 'Create Campaign',
        description: 'Craft your message with personalization tags and set delivery parameters.'
      },
      {
        title: 'Send & Monitor',
        description: 'Launch your campaign and track delivery metrics in real-time.'
      }
    ],
    benefits: [
      {
        title: 'High Open Rates',
        description: 'SMS boasts a 98% open rate, ensuring your messages are seen almost immediately.'
      },
      {
        title: 'Cost-Effective',
        description: 'Reach thousands of customers at a fraction of the cost of traditional marketing.'
      },
      {
        title: 'Instant Delivery',
        description: 'Messages are delivered within seconds, ideal for time-sensitive communications.'
      },
      {
        title: 'Increased Engagement',
        description: 'Direct and personal communication leads to higher customer engagement rates.'
      },
      {
        title: 'Improved Conversion',
        description: 'SMS campaigns typically result in higher conversion rates than email marketing.'
      }
    ],
    useCases: [
      {
        title: 'Promotional Campaigns',
        description: 'Send special offers, discounts, and promotions to drive sales and conversions.',
        icon: 'FaTag'
      },
      {
        title: 'Appointment Reminders',
        description: 'Reduce no-shows by sending automated appointment reminders to customers.',
        icon: 'FaCalendar'
      },
      {
        title: 'Order Updates',
        description: 'Keep customers informed with real-time updates on their orders and deliveries.',
        icon: 'FaTruck'
      },
      {
        title: 'Customer Feedback',
        description: 'Collect valuable feedback by sending post-purchase survey links via SMS.',
        icon: 'FaComment'
      }
    ],
    testimonials: [
      {
        quote: "Codewave's bulk SMS service has transformed our customer engagement. We've seen a 40% increase in appointment attendance since implementing their reminder system.",
        author: "Sarah Kimani",
        position: "Marketing Director",
        company: "HealthPlus Clinics",
        image: "https://randomuser.me/api/portraits/women/45.jpg"
      },
      {
        quote: "The analytics and reporting features have been invaluable for tracking our campaign performance. We can now make data-driven decisions about our messaging strategy.",
        author: "James Omondi",
        position: "Customer Success Manager",
        company: "RetailPro Kenya",
        image: "https://randomuser.me/api/portraits/men/32.jpg"
      },
      {
        quote: "Integration with our existing CRM was seamless, and the support team was incredibly helpful throughout the process. Would highly recommend!",
        author: "Grace Mwangi",
        position: "IT Director",
        company: "Savanna Financial Services",
        image: "https://randomuser.me/api/portraits/women/68.jpg"
      }
    ],
    pricing: [
      {
        name: "Starter",
        price: "15,000",
        currency: "KSh",
        interval: "month",
        description: "Perfect for small businesses just getting started with SMS marketing",
        features: [
          "5,000 SMS credits per month",
          "Basic personalization",
          "Standard delivery reports",
          "Email support",
          "API access"
        ],
        isPopular: false
      },
      {
        name: "Business",
        price: "40,000",
        currency: "KSh",
        interval: "month",
        description: "Ideal for growing businesses with regular customer communications",
        features: [
          "20,000 SMS credits per month",
          "Advanced personalization",
          "Detailed analytics dashboard",
          "Two-way messaging",
          "Priority support",
          "Full API integration"
        ],
        isPopular: true
      },
      {
        name: "Enterprise",
        price: "Custom",
        currency: "",
        interval: "month",
        description: "Tailored solutions for large organizations with high-volume requirements",
        features: [
          "Unlimited SMS credits",
          "Custom integrations",
          "Dedicated account manager",
          "24/7 support",
          "Advanced security features",
          "Custom reporting"
        ],
        isPopular: false
      }
    ],
    faqs: [
      {
        question: "How quickly are messages delivered?",
        answer: "Our SMS messages are typically delivered within seconds of sending, depending on recipient network conditions."
      },
      {
        question: "Can I personalize messages for each recipient?",
        answer: "Yes, our platform allows you to include personalization fields such as name, account number, and custom attributes in your messages."
      },
      {
        question: "Is there a limit to how many messages I can send?",
        answer: "Limits depend on your chosen plan, but we can accommodate any volume requirements through our custom enterprise solutions."
      },
      {
        question: "How do I know if my messages were delivered?",
        answer: "Our system provides comprehensive delivery reports showing the status of each message sent."
      },
      {
        question: "Can I schedule messages to be sent at specific times?",
        answer: "Yes, you can schedule campaigns to be sent at any date and time, optimizing for when your audience is most responsive."
      },
      {
        question: "Is my customer data secure?",
        answer: "Absolutely. We implement industry-leading security measures and are fully compliant with data protection regulations."
      }
    ]
  },

  {
    id: 'shortcode',
    title: 'Short Code',
    shortDescription: 'Enable customers to interact via memorable SMS short codes',
    description: 'Our Short Code services let businesses acquire a dedicated, easy-to-remember 5‑6 digit number for high‑volume two‑way SMS campaigns, voting, subscriptions, and marketing interactions.',
    color: '#E94E77',
    iconComponent: 'FaEnvelopeOpenText',
    benefitsImage: shortcode,
    features: [
      { title: 'Dedicated Short Code', description: 'A memorable number exclusive to your brand.', icon: 'FaHashtag' },
      { title: 'Two-Way Messaging', description: 'Receive replies directly through your shortcode.', icon: 'FaReply' },
      { title: 'Keyword Management', description: 'Route inbound messages based on custom keywords.', icon: 'FaTag' },
      { title: 'Auto-Reply Flows', description: 'Automate responses and subscription confirmations.', icon: 'FaRobot' },
      { title: 'Campaign Analytics', description: 'Track usage, delivery, and engagement metrics.', icon: 'FaChartLine' },
      { title: 'Opt-In Compliance', description: 'Built-in support for consent and unsubscribe mechanisms.', icon: 'FaClipboardCheck' }
    ],
    process: [
      { title: 'Provision Short Code', description: 'We apply and provision a shortcode dedicated to your organization.' },
      { title: 'Setup Keywords', description: 'Define keywords and auto-reply rules for your campaigns.' },
      { title: 'Launch Campaign', description: 'Publish your keyword in marketing materials and go live.' },
      { title: 'Track & Optimize', description: 'Monitor keyword performance and adjust flows.' }
    ],
    benefits: [
      { title: 'Brand Recall', description: 'Short codes are easy to remember, boosting recipients’ reaction rates.' },
      { title: 'Higher Opt‑In Rates', description: 'Convenient SMS opt‑in drives more subscriber growth.' },
      { title: 'Customer Insights', description: 'Analyze inbound keywords to understand interests.' },
      { title: 'Better Engagement', description: 'Two-way messaging builds stronger consumer relationships.' }
    ],
    useCases: [
      { title: 'Contest Entries', description: 'Run SMS voting or prize draw campaigns.', icon: 'FaTrophy' },
      { title: 'Subscription Services', description: 'Send curated content after opt‑in (e.g. news alerts).', icon: 'FaNewspaper' },
      { title: 'Customer Feedback', description: 'Collect ratings/reviews via simple keywords.', icon: 'FaStar' },
      { title: 'Charity Donations', description: 'Allow quick charitable giving via SMS keywords.', icon: 'FaHandsHelping' }
    ],
    testimonials: [
      { quote: "Our SMS shortcode campaign tripled our subscriber base in weeks!", author: "Mark Ochieng", position: "Head of Marketing", company: "MediaHouse", image: "https://randomuser.me/api/portraits/men/21.jpg" },
      { quote: "Keyword auto-replies made engagement so much smoother and professional.", author: "Anne Mwikali", position: "Product Lead", company: "EduLearn", image: "https://randomuser.me/api/portraits/women/22.jpg" }
    ],
    pricing: [
      { name: "Starter", price: "20,000", currency: "KSh", interval: "month", description: "Includes shortcode leasing and basic features", features: ["Shared shortcode", "Up to 5 keywords", "Delivery analytics", "Email support"], isPopular: false },
      { name: "Business", price: "60,000", currency: "KSh", interval: "month", description: "For growing campaigns", features: ["Dedicated shortcode", "30 keywords", "Auto-replies & flows", "Priority support"], isPopular: true },
      { name: "Enterprise", price: "Custom", currency: "", interval: "month", description: "Full-scale shortcode solution", features: ["Unlimited keywords", "Custom reporting", "Dedicated manager", "24/7 support"], isPopular: false }
    ],
    faqs: [
      { question: "How fast is provisioning?", answer: "Shortcodes typically take 4–8 weeks depending on mobile operator approvals." },
      { question: "Can keyword replies be customized?", answer: "Yes, you can set up custom replies, auto-replies, and follow-up flows." },
      { question: "Is it compliant with regulations?", answer: "We enforce opt‑in/out compliance per local laws and industry standards." },
      { question: "Can I use the same shortcode in multiple countries?", answer: "Shortcodes are country-specific; we can provision separate codes for each region." }
    ]
  },

  // USSD
  {
    id: 'ussd',
    title: 'USSD Services',
    shortDescription: 'Interactive, no‑internet mobile apps via dial codes',
    description: 'Our USSD platform lets customers dial short codes (e.g. *123#) to access menus—no app or internet required. Perfect for surveys, self‑service, mobile banking, airtime top‑ups and more.',
    color: '#2090B0',
    iconComponent: 'FaMobileAlt',
    benefitsImage: ussd,
    features: [
      { title: 'Menu-Based Flows', description: 'Create intuitive multi-level menus.', icon: 'FaThList' },
      { title: 'Real-Time Input', description: 'Collect data or transactions instantly.', icon: 'FaMobile' },
      { title: 'High Reach', description: 'Works on all mobile phones, no internet.', icon: 'FaGlobe' },
      { title: 'Session Control', description: 'Manage session states & timeouts.', icon: 'FaClock' },
      { title: 'Secure Transactions', description: 'SSL & session encryption for sensitive data.', icon: 'FaLock' },
      { title: 'API Integration', description: 'Connect flows to your backend systems.', icon: 'FaCode' }
    ],
    process: [
      { title: 'Procure USSD Code', description: 'We register a USSD code through mobile operators.' },
      { title: 'Design Flow', description: 'Define your menu structure and logic.' },
      { title: 'Develop Backend', description: 'Connect to APIs for transactions or data.' },
      { title: 'Test & Deploy', description: 'Deploy and refine based on testing.' }
    ],
    benefits: [
      { title: 'Widest Reach', description: 'Accessible by any GSM device without apps.' },
      { title: 'Quick Deployment', description: 'Launch services faster than app-based solutions.' },
      { title: 'Secure & Reliable', description: 'Sessions are short-lived and encrypted.' },
      { title: 'Cost-Effective', description: 'Lower development and maintenance costs.' }
    ],
    useCases: [
      { title: 'Airtime Top‑up', description: 'Top-up mobile credit directly through USSD.', icon: 'FaSimCard' },
      { title: 'Balance Enquiry', description: 'Check account/status balances in real time.', icon: 'FaBalanceScale' },
      { title: 'Polls & Surveys', description: 'Run SMS-free user surveys via simple menus.', icon: 'FaPoll' },
      { title: 'Mobile Banking', description: 'Provide secure banking functions via mobile.', icon: 'FaUniversity' }
    ],
    testimonials: [
      { quote: "We reached users in rural areas where apps don’t work—USSD delivers.", author: "Peter Njoroge", position: "Operations Manager", company: "AgriFoods Kenya", image: "https://randomuser.me/api/portraits/men/23.jpg" },
      { quote: "USSD surveys have improved our data-collection speed tenfold.", author: "Martha Wambui", position: "Marketing Analyst", company: "HealthLine", image: "https://randomuser.me/api/portraits/women/24.jpg" }
    ],
    pricing: [
      { name: "Standard", price: "30,000", currency: "KSh", interval: "month", description: "Basic USSD service", features: ["Up to 200 sessions/day", "1‑level menus", "Standard support"], isPopular: false },
      { name: "Pro", price: "80,000", currency: "KSh", interval: "month", description: "Advanced USSD capabilities", features: ["Up to 1,000 sessions/day", "Multi-level menus", "Analytics dashboard"], isPopular: true },
      { name: "Enterprise", price: "Custom", currency: "", interval: "month", description: "High-volume USSD service", features: ["Unlimited sessions", "Custom integrations", "24/7 support"], isPopular: false }
    ],
    faqs: [
      { question: "Are USSD sessions secure?", answer: "Yes, sessions are encrypted and expire quickly." },
      { question: "Can I integrate with my current systems?", answer: "Absolutely, full API support is included." },
      { question: "How many menu levels can I create?", answer: "We support 4‑level menu structures out of the box." }
    ]
  },

  // CALL CENTER & CRM
  {
    id: 'callcenter',
    title: 'Call Center & CRM',
    shortDescription: 'All-in-one call handling with integrated CRM',
    description: 'Our Call Center & CRM platform facilitates inbound/outbound voice interactions, with real-time call logging, skill-based routing, call recording, and integrated CRM for better customer management.',
    color: '#105090',
    iconComponent: 'FaHeadset',
    benefitsImage: callcenter,
    features: [
      { title: 'IVR & Routing', description: 'Automated call menus and intelligent routing.', icon: 'FaPhoneAlt' },
      { title: 'Call Recording', description: 'Record calls for quality assurance.', icon: 'FaMicrophone' },
      { title: 'Agent Dashboard', description: 'Monitor agent performance and queues.', icon: 'FaDesktop' },
      { title: 'CRM Integration', description: 'Pop up customer info on incoming calls.', icon: 'FaUserCheck' },
      { title: 'Analytics & Reporting', description: 'Track KPIs and call metrics.', icon: 'FaChartPie' },
      { title: 'Ticketing & Follow-up', description: 'Create tickets and manage callbacks.', icon: 'FaTicketAlt' }
    ],
    process: [
      { title: 'Setup IVR', description: 'Design your voice menu and routing rules.' },
      { title: 'Configure Agents', description: 'Set up user accounts and assign skills.' },
      { title: 'CRM Sync', description: 'Connect your CRM or onboard ours.' },
      { title: 'Train & Go Live', description: 'Train agents, test calls, and launch.' }
    ],
    benefits: [
      { title: 'Improved Efficiency', description: 'Smart routing reduces wait times.' },
      { title: 'Better Insights', description: 'Call analytics reveal service bottlenecks.' },
      { title: 'Higher Quality', description: 'Recordings support training & compliance.' },
      { title: 'Enhanced Experience', description: 'Agents access customer context instantly.' }
    ],
    useCases: [
      { title: 'Customer Support', description: 'Handle support calls with SLA tracking.', icon: 'FaHeadset' },
      { title: 'Sales Outreach', description: 'Manage outbound calling campaigns.', icon: 'FaBullhorn' },
      { title: 'Follow-ups', description: 'Schedule callbacks and reminders.', icon: 'FaCalendarCheck' },
      { title: 'Surveys', description: 'Run voice surveys post-call.', icon: 'FaSurvey' }
    ],
    testimonials: [
      { quote: "Call handling times dropped 30% after implementing routing!", author: "David Okello", position: "Support Manager", company: "TechServe", image: "https://randomuser.me/api/portraits/men/25.jpg" },
      { quote: "We unified our CRM and call flow—customer satisfaction improved greatly.", author: "Jane Wanjiku", position: "Head of CX", company: "FinSecure", image: "https://randomuser.me/api/portraits/women/26.jpg" }
    ],
    pricing: [
      { name: "Basic", price: "50,000", currency: "KSh", interval: "month", description: "Essential call center tools", features: ["3 agents", "IVR menu", "Call logs"], isPopular: false },
      { name: "Business", price: "150,000", currency: "KSh", interval: "month", description: "Full-featured solution", features: ["10 agents", "CRM integration", "Analytics"], isPopular: true },
      { name: "Enterprise", price: "Custom", currency: "", interval: "month", description: "Scalable setup", features: ["Unlimited agents", "Dedicated VoIP", "SLA agreement"], isPopular: false }
    ],
    faqs: [
      { question: "Can I integrate my CRM?", answer: "Yes, we support Salesforce, HubSpot, Zoho, and custom CRM systems." },
      { question: "Is call recording included?", answer: "Yes—storage and playback are built into every plan." },
      { question: "Can I scale agent count dynamically?", answer: "Absolutely. Add/remove agents without downtime." }
    ]
  },

  // SOFTWARE DEVELOPMENT
  {
    id: 'software',
    title: 'Software Development',
    shortDescription: 'Custom software tailored to your business needs',
    description: 'From internal tools and customer portals to mobile/web apps, our software development is tailored to your goals, using best-in-class tech stacks and agile processes to deliver efficient, maintainable solutions.',
    color: '#3A3D98',
    iconComponent: 'FaLaptopCode',
    benefitsImage: software,
    features: [
      { title: 'Custom Web & Mobile', description: 'We build apps tailored to your use-case.', icon: 'FaMobileAlt' },
      { title: 'Agile Methodology', description: 'Iterative development with regular reviews.', icon: 'FaProjectDiagram' },
      { title: 'Quality Assurance', description: 'Automated and manual testing for reliability.', icon: 'FaBug' },
      { title: 'Scalable Architecture', description: 'Built to grow with your business.', icon: 'FaCloud' },
      { title: 'UI/UX Design', description: 'Human-centered designs for great usability.', icon: 'FaPaintBrush' },
      { title: 'Maintenance & Support', description: 'Ongoing updates and technical support.', icon: 'FaLifeRing' }
    ],
    process: [
      { title: 'Discovery', description: 'Understand goals, users, and challenges.' },
      { title: 'Design', description: 'Create wireframes, prototypes, and visual designs.' },
      { title: 'Development', description: 'Build features in sprints with demos.' },
      { title: 'QA & Testing', description: 'Ensure code quality and performance.' },
      { title: 'Launch & Support', description: 'Deploy to production and provide support.' }
    ],
    benefits: [
      { title: 'Tailored Fit', description: 'Your solution, not off‑the‑shelf.' },
      { title: 'Competitive Advantage', description: 'Unique features differentiate your brand.' },
      { title: 'Operational Efficiency', description: 'Automate workflows and reduce manual effort.' },
      { title: 'Scalable & Secure', description: 'Grow without compromise.' }
    ],
    useCases: [
      { title: 'Customer Portals', description: 'Self-service interfaces for clients.', icon: 'FaUserCircle' },
      { title: 'Internal Tools', description: 'Dashboards, automation, and reporting apps.', icon: 'FaTools' },
      { title: 'Mobile Apps', description: 'Native or cross-platform mobile experiences.', icon: 'FaMobile' },
      { title: 'e-Commerce Platforms', description: 'Sell directly through your branded shop.', icon: 'FaShoppingCart' }
    ],
    testimonials: [
      { quote: "Their agile approach kept us on track and on budget.", author: "Laura Kiptoo", position: "Product Manager", company: "EduTech", image: "https://randomuser.me/api/portraits/women/27.jpg" },
      { quote: "Our customer portal has improved service efficiency by 50%.", author: "Patrick Otieno", position: "CTO", company: "MediSoft", image: "https://randomuser.me/api/portraits/men/28.jpg" }
    ],
    pricing: [
      { name: "Kickstart", price: "150,000", currency: "KSh", interval: "project", description: "MVP or small app", features: ["UX/UI design", "Core features", "1 QA cycle"], isPopular: false },
      { name: "Growth", price: "500,000", currency: "KSh", interval: "project", description: "Mid-sized application", features: ["All Kickstart features", "2 QA cycles", "Analytics integration"], isPopular: true },
      { name: "Enterprise", price: "Custom", currency: "", interval: "project", description: "Complex, large-scale projects", features: ["Dedicated team", "CI/CD", "24/7 support"], isPopular: false }
    ],
    faqs: [
      { question: "What tech stacks do you use?", answer: "We use React, Node.js, Python, .NET, mobile frameworks, and more." },
      { question: "How is pricing estimated?", answer: "Based on scope, timeline, complexity, and features." },
      { question: "Do you provide support services?", answer: "Yes — maintenance and 24/7 support can be bundled." }
    ]
  },

  // SYSTEM INTEGRATION
  {
    id: 'integration',
    title: 'System Integration',
    shortDescription: 'Connect all your systems into cohesive, automated workflows',
    description: 'Eliminate silos by integrating CRMs, ERPs, payment gateways, logistics systems, and more into a unified infrastructure that synchronizes data, automates business logic, and reduces errors.',
    color: '#42A5F5',
    iconComponent: 'FaProjectDiagram',
    benefitsImage: integration,
    features: [
      { title: 'API Connectors', description: 'Prebuilt integrations for common platforms.', icon: 'FaPlug' },
      { title: 'Custom Integrations', description: 'Tailor-built connectors for niche systems.', icon: 'FaCog' },
      { title: 'Workflow Automation', description: 'Trigger workflows based on events.', icon: 'FaSyncAlt' },
      { title: 'Data Mapping', description: 'Transform data between schemas.', icon: 'FaExchangeAlt' },
      { title: 'Error Handling', description: 'Retry policies and alerting built in.', icon: 'FaExclamationTriangle' },
      { title: 'Monitoring & Logs', description: 'Track integration health and performance.', icon: 'FaEye' }
    ],
    process: [
      { title: 'Audit Systems', description: 'Understand existing workflows and data flows.' },
      { title: 'Design Integration', description: 'Map endpoints, formats, and triggers.' },
      { title: 'Develop Connectors', description: 'Build and test integration scripts.' },
      { title: 'Deploy & Monitor', description: 'Launch connectors and configure monitoring.' }
    ],
    benefits: [
      { title: 'Unified Data', description: 'Eliminate data discrepancies between systems.' },
      { title: 'Process Efficiency', description: 'Automate handoffs and reduce manual tasks.' },
      { title: 'Better Visibility', description: 'Dashboards aggregate data from all systems.' },
      { title: 'Scalable Architecture', description: 'Easily add new integrations over time.' }
    ],
    useCases: [
      { title: 'CRM ↔ ERP Sync', description: 'Sync customer data and order status.', icon: 'FaExchangeAlt' },
      { title: 'Order Fulfillment', description: 'Automate shipping and invoicing flows.', icon: 'FaTruckLoading' },
      { title: 'Payment Reconciliation', description: 'Track and reconcile payments automatically.', icon: 'FaMoneyBillWave' },
      { title: 'Notifications', description: 'Send alerts when key events occur.', icon: 'FaBell' }
    ],
    testimonials: [
      { quote: "Integration saved us 20 hours/week in manual work!", author: "Catherine Wainaina", position: "Operations Director", company: "BuildSmart", image: "https://randomuser.me/api/portraits/women/29.jpg" },
      { quote: "Data flows are now seamless between our CRM and accounting.", author: "Eric Kamau", position: "Head of IT", company: "ConstructCo", image: "https://randomuser.me/api/portraits/men/30.jpg" }
    ],
    pricing: [
      { name: "Starter", price: "75,000", currency: "KSh", interval: "project", description: "Single system integration", features: ["1 connector", "Basic monitoring", "Email support"], isPopular: false },
      { name: "Pro", price: "200,000", currency: "KSh", interval: "project", description: "Multiple system sync", features: ["Up to 3 connectors", "Dashboard + alerts", "Phone support"], isPopular: true },
      { name: "Enterprise", price: "Custom", currency: "", interval: "project", description: "Full integration suite", features: ["Unlimited connectors", "Dedicated engineer", "24/7 SLA"], isPopular: false }
    ],
    faqs: [
      { question: "Which systems can you integrate?", answer: "CRMs, ERPs, payment platforms, logistics, legacy systems and more." },
      { question: "How quickly can you deliver?", answer: "Typical take 4–6 weeks for Pro-level integrations." },
      { question: "What if an API changes?", answer: "We monitor versions and offer maintenance & updates as needed." }
    ]
  },

  // PAYMENT SOLUTIONS
  {
    id: 'payment',
    title: 'Payment Solutions',
    shortDescription: 'Secure and convenient mobile & card payments',
    description: 'We integrate STK push, mobile money, card, and bank payments into your systems, offering a seamless checkout experience with real-time reconciliation and fraud protection.',
    color: '#00C853',
    iconComponent: 'FaMoneyCheckAlt',
    benefitsImage: payment,
    features: [
      { title: 'STK Push & Lipa na MPESA', description: 'Trigger secure mobile payments in-app or on-site.', icon: 'FaMobileAlt' },
      { title: 'Card Processing', description: 'Accept Visa, Mastercard and M-PESA global payments.', icon: 'FaCreditCard' },
      { title: 'Mobile Wallets', description: 'Support popular wallets like Airtel Money.', icon: 'FaWallet' },
      { title: 'Real‑Time Reconciliation', description: 'Automate payment matching back‑end.', icon: 'FaSync' },
      { title: 'Fraud Protection', description: 'Chargeback & risk monitoring tools.', icon: 'FaShieldAlt' },
      { title: 'Developer API', description: 'Integrate with your platform effortlessly.', icon: 'FaCode' }
    ],
    process: [
      { title: 'Onboard Merchant', description: 'Set up merchant account and compliance.' },
      { title: 'Integrate SDK/API', description: 'Add payment logic to your app or website.' },
      { title: 'Test Transactions', description: 'Run sandbox tests before going live.' },
      { title: 'Go Live & Monitor', description: 'Launch payments and track performance.' }
    ],
    benefits: [
      { title: 'Faster Checkout', description: 'Reduce friction with mobile-money taps.' },
      { title: 'Higher Conversion', description: 'Optimized payment UI converts more sales.' },
      { title: 'Secure & Compliant', description: 'KYC, PCI, fraud tools included.' },
      { title: 'Flexible Options', description: 'Support for multiple payment instruments.' }
    ],
    useCases: [
      { title: 'e-Commerce', description: 'Card and mobile payments on checkout.', icon: 'FaShoppingBasket' },
      { title: 'Utility Bill Pay', description: 'Instant bill settlement via mobile.', icon: 'FaRegCreditCard' },
      { title: 'In-App Payments', description: 'Enable purchases directly inside your app.', icon: 'FaMobile' },
      { title: 'Donations', description: 'Accept recurring or one-time charitable donations.', icon: 'FaDonate' }
    ],
    testimonials: [
      { quote: "Our conversion rate jumped 25% after adding M-PESA integration!", author: "Bella Ndegwa", position: "E‑commerce Manager", company: "ShopPlus", image: "https://randomuser.me/api/portraits/women/31.jpg" },
      { quote: "STK push checkout is seamless and builds customer trust.", author: "Brian Otieno", position: "CTO", company: "GovServices", image: "https://randomuser.me/api/portraits/men/32.jpg" }
    ],
    pricing: [
      { name: "Standard", price: "20,000", currency: "KSh", interval: "month", description: "Basic payment integrations", features: ["STK push", "1 mobile wallet", "Basic reconciliation"], isPopular: false },
      { name: "Business", price: "60,000", currency: "KSh", interval: "month", description: "Full-featured solution", features: ["All wallets & cards", "Real-time reconciliation", "API access"], isPopular: true },
      { name: "Enterprise", price: "Custom", currency: "", interval: "month", description: "Advanced payment orchestration", features: ["Multi-country support", "Fraud module", "Dedicated support"], isPopular: false }
    ],
    faqs: [
      { question: "Can I support Visa and mobile money?", answer: "Yes — our system handles cards and mobile wallets seamlessly." },
      { question: "Do you support recurring payments?", answer: "Yes, we support subscription billing and recurring transactions." },
      { question: "Am I PCI compliant?", answer: "Yes — our gateway is fully PCI‑DSS compliant." }
    ]
  },

  // IVR & VOICE
  {
    id: 'ivr',
    title: 'IVR & Voice',
    shortDescription: 'Interactive voice systems to automate customer calls',
    description: 'Our IVR and Voice platform lets callers navigate phone menus, connect with departments, and complete voice-driven tasks like surveys or payments—all via voice or keypad—to reduce load on live agents.',
    color: '#673AB7',
    iconComponent: 'FaPhoneVolume',
    benefitsImage: ivr,
    features: [
      { title: 'Custom IVR Flows', description: 'Design voice menus with DTMF or speech.', icon: 'FaListAlt' },
      { title: 'Speech Recognition', description: 'Self-service via voice commands.', icon: 'FaMicrophoneAlt' },
      { title: 'Call Transfer', description: 'Route calls to agents or departments.', icon: 'FaShareAlt' },
      { title: 'Voicemail Capture', description: 'Store and email voice messages.', icon: 'FaVoicemail' },
      { title: 'Click‑to‑Call', description: 'Trigger calls from your website/app.', icon: 'FaMousePointer' },
      { title: 'Call Analytics', description: 'Track call times, dropouts and usage.', icon: 'FaChartArea' }
    ],
    process: [
      { title: 'IVR Design', description: 'Map voice menus and logic.' },
      { title: 'Script Recording', description: 'Record prompts with professional voice talent.' },
      { title: 'Backend Integration', description: 'Connect to databases or APIs.' },
      { title: 'Testing & Launch', description: 'Test call flows end-to-end and deploy.' }
    ],
    benefits: [
      { title: 'Reduced Live Calls', description: 'Let self-service handle common queries.' },
      { title: '24/7 Availability', description: 'IVR never closes—customers can always reach you.' },
      { title: 'Better Routing', description: 'Send callers to the right agents automatically.' },
      { title: 'Cost Efficient', description: 'Save on live-agent hours.' }
    ],
    useCases: [
      { title: 'Customer Self-Service', description: 'Check status, balances, or FAQs via voice.', icon: 'FaQuestionCircle' },
      { title: 'Appointment Booking', description: 'Let customers schedule via IVR.', icon: 'FaCalendarPlus' },
      { title: 'Surveys', description: 'Collect feedback via keypad or voice.', icon: 'FaPollH' },
      { title: 'Click-to-Call', description: 'One-click call from your website/app.', icon: 'FaMousePointer' }
    ],
    testimonials: [
      { quote: "Customers love booking appointments via phone—no staff needed!", author: "Susan Kariuki", position: "Clinic Manager", company: "CareWell Clinics", image: "https://randomuser.me/api/portraits/women/33.jpg" },
      { quote: "Our IVR cuts call volumes by 40%, freeing agents for complex queries.", author: "Michael Gitau", position: "Support Director", company: "FinAid", image: "https://randomuser.me/api/portraits/men/34.jpg" }
    ],
    pricing: [
      { name: "Starter", price: "25,000", currency: "KSh", interval: "month", description: "Basic IVR setup", features: ["2‑menu levels", "Standard voice prompts", "Call analytics"], isPopular: false },
      { name: "Business", price: "70,000", currency: "KSh", interval: "month", description: "Advanced IVR system", features: ["4‑level menus", "speech recognition", "call recording"], isPopular: true },
      { name: "Enterprise", price: "Custom", currency: "", interval: "month", description: "High-volume voice and IVR", features: ["Unlimited menus", "Live agent handover", "24/7 monitoring"], isPopular: false }
    ],
    faqs: [
      { question: "Can IVR recognize speech?", answer: "Yes—we support DTMF and speech‑to‑text for menus." },
      { question: "Can callers request callbacks?", answer: "Yes—IVR can route to voicemail or callback requests." },
      { question: "What languages do you support?", answer: "We support English, Swahili, and can add custom voice prompts." }
    ]
  }


];

// === Animations ===
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const fadeInRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

// === Styled Components ===
const PageWrapper = styled.div`
margin-top:10vh;
  background-color: white;
  overflow: hidden;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

// Loading & Error Styles
const LoadingOverlay = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100vh;
  font-size: 20px;
  color: #105090;
`;

const ErrorMessage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100vh;
  font-size: 20px;
  color: #2090B0;
`;

// Hero Section
const HeroSection = styled.section`
  padding: 100px 0;
  background: ${props => props.bgColor ? `linear-gradient(135deg, ${props.bgColor}, ${props.bgColor}CC)` : 'linear-gradient(135deg, #105090, #105090CC)'};
  color: white;
  position: relative;
  
  opacity: ${props => props.isVisible ? 1 : 0};
  transform: ${props => props.isVisible ? 'translateY(0)' : 'translateY(20px)'};
  transition: opacity 0.6s ease, transform 0.6s ease;
  
  @media (max-width: 768px) {
    padding: 80px 0;
  }
`;

const HeroContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
`;

const BreadcrumbNav = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 30px;
  font-size: 14px;
`;

const BreadcrumbLink = styled.a`
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  transition: color 0.3s;
  
  &:hover {
    color: white;
  }
`;

const BreadcrumbSeparator = styled.span`
  margin: 0 10px;
  color: rgba(255, 255, 255, 0.5);
`;

const BreadcrumbCurrent = styled.span`
  color: white;
  font-weight: 500;
`;

const ServiceIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  margin: 0 auto 25px;
`;

const HeroTitle = styled.h1`
  font-size: 48px;
  font-weight: 700;
  margin-bottom: 20px;
  line-height: 1.2;
  
  @media (max-width: 768px) {
    font-size: 36px;
  }
  
  @media (max-width: 576px) {
    font-size: 30px;
  }
`;

const HeroDescription = styled.p`
  font-size: 18px;
  line-height: 1.7;
  opacity: 0.9;
  max-width: 700px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

// Section Components
const FeaturesSection = styled.section`
  padding: 100px 0;
  background-color: white;
  
  opacity: ${props => props.isVisible ? 1 : 0};
  transform: ${props => props.isVisible ? 'translateY(0)' : 'translateY(20px)'};
  transition: opacity 0.6s ease, transform 0.6s ease;
  
  @media (max-width: 768px) {
    padding: 70px 0;
  }
`;

const HowItWorksSection = styled.section`
  padding: 100px 0;
  background-color: #f8f9fb;
  
  opacity: ${props => props.isVisible ? 1 : 0};
  transform: ${props => props.isVisible ? 'translateY(0)' : 'translateY(20px)'};
  transition: opacity 0.6s ease, transform 0.6s ease;
  
  @media (max-width: 768px) {
    padding: 70px 0;
  }
`;

const BenefitsSection = styled.section`
  padding: 100px 0;
  background-color: white;
  
  opacity: ${props => props.isVisible ? 1 : 0};
  transform: ${props => props.isVisible ? 'translateY(0)' : 'translateY(20px)'};
  transition: opacity 0.6s ease, transform 0.6s ease;
  
  @media (max-width: 768px) {
    padding: 70px 0;
  }
`;

const UseCasesSection = styled.section`
  padding: 100px 0;
  background-color: #f8f9fb;
  
  opacity: ${props => props.isVisible ? 1 : 0};
  transform: ${props => props.isVisible ? 'translateY(0)' : 'translateY(20px)'};
  transition: opacity 0.6s ease, transform 0.6s ease;
  
  @media (max-width: 768px) {
    padding: 70px 0;
  }
`;

const TestimonialsSection = styled.section`
  padding: 100px 0;
  background: ${props => props.bgColor ? `linear-gradient(135deg, ${props.bgColor}, ${props.bgColor}CC)` : 'linear-gradient(135deg, #105090, #105090CC)'};
  color: white;
  
  opacity: ${props => props.isVisible ? 1 : 0};
  transform: ${props => props.isVisible ? 'translateY(0)' : 'translateY(20px)'};
  transition: opacity 0.6s ease, transform 0.6s ease;
  
  @media (max-width: 768px) {
    padding: 70px 0;
  }
`;

const PricingSection = styled.section`
  padding: 100px 0;
  background-color: white;
  
  opacity: ${props => props.isVisible ? 1 : 0};
  transform: ${props => props.isVisible ? 'translateY(0)' : 'translateY(20px)'};
  transition: opacity 0.6s ease, transform 0.6s ease;
  
  @media (max-width: 768px) {
    padding: 70px 0;
  }
`;

const FAQSection = styled.section`
  padding: 100px 0;
  background-color: #f8f9fb;
  
  opacity: ${props => props.isVisible ? 1 : 0};
  transform: ${props => props.isVisible ? 'translateY(0)' : 'translateY(20px)'};
  transition: opacity 0.6s ease, transform 0.6s ease;
  
  @media (max-width: 768px) {
    padding: 70px 0;
  }
`;

const RelatedServicesSection = styled.section`
  padding: 100px 0;
  background-color: white;
  
  opacity: ${props => props.isVisible ? 1 : 0};
  transform: ${props => props.isVisible ? 'translateY(0)' : 'translateY(20px)'};
  transition: opacity 0.6s ease, transform 0.6s ease;
  
  @media (max-width: 768px) {
    padding: 70px 0;
  }
`;

const CTASection = styled.section`
  padding: 100px 0;
  background: ${props => props.bgColor ? `linear-gradient(135deg, ${props.bgColor}, ${props.bgColor}CC)` : 'linear-gradient(135deg, #105090, #105090CC)'};
  color: white;
  
  opacity: ${props => props.isVisible ? 1 : 0};
  transform: ${props => props.isVisible ? 'translateY(0)' : 'translateY(20px)'};
  transition: opacity 0.6s ease, transform 0.6s ease;
  
  @media (max-width: 768px) {
    padding: 70px 0;
  }
`;

// Common Section Header
const SectionHeader = styled.div`
  text-align: center;
  max-width: 800px;
  margin: 0 auto 60px;
  
  @media (max-width: 768px) {
    margin-bottom: 40px;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 14px;
  font-weight: 700;
  color: ${props => props.light ? 'rgba(255, 255, 255, 0.8)' : '#2090B0'};
  margin-bottom: 15px;
  letter-spacing: 1px;
  text-transform: uppercase;
`;

const SectionTitle = styled.h2`
  font-size: 36px;
  font-weight: 700;
  color: ${props => props.light ? 'white' : '#105090'};
  margin-bottom: 20px;
  line-height: 1.3;
  
  @media (max-width: 768px) {
    font-size: 30px;
  }
  
  @media (max-width: 576px) {
    font-size: 26px;
  }
`;

const SectionDescription = styled.p`
  font-size: 18px;
  line-height: 1.7;
  color: ${props => props.light ? 'rgba(255, 255, 255, 0.9)' : '#666'};
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

// Features Grid
const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled.div`
  background: white;
  border-radius: 10px;
  padding: 30px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  
  opacity: 0;
  animation: ${fadeIn} 0.6s ease forwards;
  animation-delay: ${props => props.delay}s;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
    
    div:first-child {
      background: ${props => props.color};
    }
  }
`;

const FeatureIcon = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: rgba(${props => {
    const hexToRgb = (hex) => {
      const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(
        hex.replace(shorthandRegex, (m, r, g, b) => r + r + g + g + b + b)
      );
      return result
        ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`
        : '16, 80, 144';
    };
    return hexToRgb(props.color || '#105090');
  }}, 0.1);
  color: ${props => props.color || '#105090'};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  font-size: 24px;
  transition: all 0.3s ease;
`;

const FeatureTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 15px;
  color: #105090;
`;

const FeatureDescription = styled.p`
  font-size: 15px;
  line-height: 1.6;
  color: #666;
`;

// Process Steps
const ProcessSteps = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const ProcessStep = styled.div`
  display: flex;
  margin-bottom: 50px;
  opacity: 0;
  animation: ${fadeIn} 0.6s ease forwards;
  animation-delay: ${props => props.delay}s;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  @media (max-width: 576px) {
    flex-direction: column;
  }
`;

const StepNumber = styled.div`
  width: 60px;
  height: 60px;
  min-width: 60px;
  border-radius: 50%;
  background: ${props => props.color || '#105090'};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  font-weight: 700;
  margin-right: 25px;
  
  @media (max-width: 576px) {
    margin-bottom: 15px;
    margin-right: 0;
  }
`;

const StepContent = styled.div`
  flex: 1;
  padding-top: 5px;
`;

const StepTitle = styled.h3`
  font-size: 22px;
  font-weight: 600;
  margin-bottom: 10px;
  color: #105090;
`;

const StepDescription = styled.p`
  font-size: 16px;
  line-height: 1.6;
  color: #666;
`;

// Benefits Section
const TwoColumnLayout = styled.div`
  display: flex;
  align-items: center;
  gap: 50px;
  
  @media (max-width: 992px) {
    flex-direction: column;
  }
`;

const BenefitsContent = styled.div`
  flex: 1;
`;

const BenefitsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 25px;
  margin-top: 40px;
`;

const BenefitItem = styled.div`
  display: flex;
  align-items: flex-start;
  opacity: 0;
  animation: ${fadeInLeft} 0.6s ease forwards;
  animation-delay: ${props => props.isVisible ? props.delay + 0.3 : 0}s;
`;

const BenefitCheck = styled.div`
  width: 24px;
  height: 24px;
  min-width: 24px;
  border-radius: 50%;
  background: ${props => props.color || '#105090'};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  margin-right: 15px;
  margin-top: 3px;
`;

const BenefitTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 5px;
  color: #105090;
`;

const BenefitDescription = styled.p`
  font-size: 15px;
  line-height: 1.6;
  color: #666;
`;

const BenefitsImage = styled.div`
  flex: 1;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  transform: translateX(30px);
  opacity: 0;
  animation: ${fadeInRight} 0.8s ease forwards 0.5s;
  
  img {
    width: 100%;
    height: auto;
    display: block;
  }
  
  @media (max-width: 992px) {
    max-width: 600px;
    width: 100%;
  }
`;

// Use Cases
const UseCasesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 30px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const UseCaseCard = styled.div`
  background: white;
  border-radius: 10px;
  padding: 30px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  
  opacity: 0;
  animation: ${fadeIn} 0.6s ease forwards;
  animation-delay: ${props => props.delay}s;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
  }
`;

const UseCaseIcon = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: ${props => props.color || '#105090'};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  font-size: 24px;
`;

const UseCaseTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 15px;
  color: #105090;
`;

const UseCaseDescription = styled.p`
  font-size: 15px;
  line-height: 1.6;
  color: #666;
`;

// Testimonials
const TestimonialsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const TestimonialCard = styled.div`
  background: white;
  border-radius: 10px;
  padding: 30px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  position: relative;
  
  opacity: 0;
  animation: ${fadeIn} 0.6s ease forwards;
  animation-delay: ${props => props.delay}s;
`;

const QuoteIcon = styled.div`
  position: absolute;
  top: 20px;
  right: 20px;
  color: #f0f0f0;
  font-size: 28px;
`;

const TestimonialText = styled.p`
  font-size: 15px;
  line-height: 1.7;
  color: #555;
  margin-bottom: 25px;
  font-style: italic;
`;

const TestimonialAuthorSection = styled.div`
  display: flex;
  align-items: center;
`;

const TestimonialAuthorImage = styled.img`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  object-fit: cover;
  margin-right: 15px;
`;

const TestimonialAuthorInfo = styled.div``;

const TestimonialAuthor = styled.h4`
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 5px;
  color: #105090;
`;

const TestimonialCompany = styled.p`
  font-size: 14px;
  color: #777;
  margin: 0;
`;

// Pricing
const PricingGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const PricingCard = styled.div`
  background: white;
  border-radius: 15px;
  padding: ${props => props.isPopular ? '40px 30px' : '30px'};
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  border: ${props => props.isPopular ? '2px solid #2090B0' : '1px solid #f0f0f0'};
  position: relative;
  transform: ${props => props.isPopular ? 'scale(1.05)' : 'scale(1)'};
  z-index: ${props => props.isPopular ? '2' : '1'};
  transition: all 0.3s ease;
  
  opacity: 0;
  animation: ${fadeIn} 0.6s ease forwards;
  animation-delay: ${props => props.delay}s;
  
  &:hover {
    transform: ${props => props.isPopular ? 'scale(1.07)' : 'scale(1.02)'};
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  }
  
  @media (max-width: 992px) {
    transform: scale(1);
    
    &:hover {
      transform: scale(1.02);
    }
  }
`;

const PopularTag = styled.div`
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  background: #2090B0;
  color: white;
  font-size: 12px;
  font-weight: 600;
  padding: 5px 15px;
  border-radius: 20px;
`;

const PlanName = styled.h3`
  font-size: 22px;
  font-weight: 600;
  margin-bottom: 15px;
  color: #105090;
  text-align: center;
`;

const PlanPrice = styled.div`
  text-align: center;
  margin-bottom: 20px;
`;

const PriceAmount = styled.span`
  font-size: 36px;
  font-weight: 700;
  color: #105090;
`;

const PriceInterval = styled.span`
  font-size: 16px;
  color: #777;
`;

const PlanDescription = styled.p`
  font-size: 15px;
  line-height: 1.6;
  color: #666;
  text-align: center;
  margin-bottom: 25px;
`;

const PlanFeatures = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 30px;
`;

const PlanFeature = styled.li`
  font-size: 15px;
  color: #555;
  padding: 10px 0;
  display: flex;
  align-items: center;
`;

const FeatureCheckmark = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  min-width: 20px;
  border-radius: 50%;
  background: ${props => props.color || '#105090'};
  color: white;
  font-size: 10px;
  margin-right: 10px;
`;

const PlanButton = styled.a`
  display: block;
  width: 100%;
  padding: 12px;
  text-align: center;
  background: ${props => props.color || 'transparent'};
  color: ${props => props.textColor || '#105090'};
  border: 2px solid ${props => props.borderColor || '#105090'};
  border-radius: 50px;
  font-size: 16px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.08);
    opacity: 0.9;
  }
`;

// FAQ
const FAQGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 30px;
  max-width: 1000px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FAQ = styled.div`
  opacity: 0;
  animation: ${fadeIn} 0.6s ease forwards;
  animation-delay: ${props => props.delay}s;
`;

const FAQQuestion = styled.h3`
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 10px;
  color: #105090;
`;

const FAQAnswer = styled.p`
  font-size: 15px;
  line-height: 1.6;
  color: #666;
`;

const MoreQuestionsSection = styled.div`
  text-align: center;
  margin-top: 60px;
`;

const MoreQuestionsText = styled.p`
  font-size: 18px;
  margin-bottom: 20px;
  color: #555;
`;

const MoreQuestionsButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 12px 30px;
  background: ${props => props.color || '#105090'};
  color: white;
  font-size: 16px;
  font-weight: 600;
  border-radius: 50px;
  text-decoration: none;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
    opacity: 0.9;
  }
`;

// Related Services
const RelatedServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const RelatedServiceCard = styled.div`
  background: white;
  border-radius: 15px;
  padding: 30px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  
  opacity: 0;
  animation: ${fadeIn} 0.6s ease forwards;
  animation-delay: ${props => props.delay}s;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  }
`;

const RelatedServiceIcon = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: ${props => props.color || '#105090'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: white;
  margin-bottom: 20px;
`;

const RelatedServiceName = styled.h3`
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 15px;
  color: #105090;
`;

const RelatedServiceDescription = styled.p`
  font-size: 15px;
  line-height: 1.6;
  color: #666;
  margin-bottom: 20px;
`;

const RelatedServiceLink = styled.a`
  display: inline-flex;
  align-items: center;
  font-size: 15px;
  font-weight: 600;
  color: ${props => props.color || '#105090'};
  text-decoration: none;
  transition: all 0.3s ease;
  
  svg {
    font-size: 12px;
    margin-left: 6px;
    transition: transform 0.3s ease;
  }
  
  &:hover {
    svg {
      transform: translateX(3px);
    }
  }
`;

// CTA
const CTAContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
`;

const CTATitle = styled.h2`
  font-size: 36px;
  font-weight: 700;
  margin-bottom: 20px;
  
  @media (max-width: 768px) {
    font-size: 30px;
  }
  
  @media (max-width: 576px) {
    font-size: 26px;
  }
`;

const CTADescription = styled.p`
  font-size: 18px;
  line-height: 1.7;
  margin-bottom: 30px;
  opacity: 0.9;
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const CTAButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 15px 40px;
  background: white;
  color: #105090;
  font-size: 16px;
  font-weight: 600;
  border-radius: 50px;
  text-decoration: none;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.15);
  }
`;
